import { Button } from '@heroui/button';
import { Form } from '@heroui/form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import type { z } from 'zod';
import { ControlledEmailInput } from '@/components/auth/controlled-email-input';
import { FormSubmitButton } from '@/components/auth/form-submit-button';
import { authClient } from '@/lib/auth-client';
import { useLoadingCallback } from '@/lib/hooks/use-loading-callback';
import { authToasts } from '@/lib/utils/toast';
import { emailSchema } from '@/schemas/auth';

interface OtpRequestFormProps {
    onOtpSent: (email: string) => void;
    onSwitchToPassword: () => void;
}

export function OtpRequestForm({
    onOtpSent,
    onSwitchToPassword,
}: OtpRequestFormProps) {
    const otpRequestForm = useForm<z.infer<typeof emailSchema>>({
        resolver: zodResolver(emailSchema),
        mode: 'onChange', // Real-time validation
        defaultValues: {
            email: '',
        },
    });

    const [handleRequestOtp, requestingOtp] = useLoadingCallback(
        async (data: z.infer<typeof emailSchema>) => {
            const response = await authClient.emailOtp.sendVerificationOtp({
                email: data.email,
                type: 'sign-in',
            });

            if (response.error) {
                authToasts.otpFailed(
                    response.error.message || 'Failed to send OTP'
                );
            } else {
                authToasts.otpSent();
                onOtpSent(data.email);
            }
        }
    );

    return (
        <div className="flex flex-col gap-4">
            <Form
                className="flex flex-col gap-2"
                onSubmit={otpRequestForm.handleSubmit(handleRequestOtp)}
            >
                <ControlledEmailInput
                    control={otpRequestForm.control}
                    name="email"
                />
                <FormSubmitButton
                    color="primary"
                    control={otpRequestForm.control}
                    fullWidth
                    isLoading={requestingOtp}
                >
                    Send OTP
                </FormSubmitButton>
            </Form>
            <Button onPress={onSwitchToPassword} variant="bordered">
                Sign in with password
            </Button>
        </div>
    );
}
